import { useCallback, useEffect, useMemo, useState } from "react";

import {
    createRecurringInvoice,
    deleteRecurringInvoice,
    getArticles,
    getCustomers,
    getProfil,
    getRecurringInvoices,
    updateRecurringInvoice,
} from "../../../api";
import { logError } from "../../../logging/loggingService";
import {
    buildEmptyForm,
    sortArticlesByName,
    toFormModel,
    toPayload,
} from "./recurringInvoiceUtils";

export default function useRecurringInvoiceManagement() {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [customers, setCustomers] = useState([]);
    const [articles, setArticles] = useState([]);
    const [profil, setProfil] = useState(null);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState("create");
    const [form, setForm] = useState(buildEmptyForm());
    const [saving, setSaving] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [articleErrors, setArticleErrors] = useState({});

    const [expandedLogs, setExpandedLogs] = useState(null);
    const [toast, setToast] = useState({ open: false, msg: "", severity: "success" });

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [custRes, artRes, recurringRes, profilRes] = await Promise.all([
                getCustomers(),
                getArticles(),
                getRecurringInvoices(),
                getProfil(),
            ]);
            setCustomers(Array.isArray(custRes.data) ? custRes.data : []);
            setArticles(Array.isArray(artRes.data) ? artRes.data : []);
            setEntries(Array.isArray(recurringRes.data) ? recurringRes.data : []);
            setProfil(profilRes?.data || null);
        } catch (err) {
            logError("Fehler beim Laden der wiederkehrenden Rechnungen", err, { component: "RecurringInvoiceManagement" });
            setError("Daten konnten nicht geladen werden.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const resetForm = useCallback(() => {
        setForm(buildEmptyForm());
        setDialogMode("create");
        setFieldErrors({});
        setArticleErrors({});
    }, []);

    const openCreateDialog = useCallback(() => {
        resetForm();
        setDialogMode("create");
        setFieldErrors({});
        setArticleErrors({});
        setDialogOpen(true);
    }, [resetForm]);

    const openEditDialog = useCallback((entry) => {
        setForm(toFormModel(entry));
        setDialogMode("edit");
        setFieldErrors({});
        setArticleErrors({});
        setDialogOpen(true);
    }, []);

    const closeDialog = useCallback(() => {
        setDialogOpen(false);
    }, []);

    const clearFieldError = useCallback((field) => {
        setFieldErrors((prev) => {
            if (!prev[field]) return prev;
            const next = { ...prev };
            delete next[field];
            return next;
        });
    }, []);

    const clearArticleError = useCallback((index, field) => {
        setArticleErrors((prev) => {
            if (!prev[index] || !prev[index][field]) return prev;
            const next = { ...prev, [index]: { ...prev[index] } };
            delete next[index][field];
            if (Object.keys(next[index]).length === 0) {
                delete next[index];
            }
            return next;
        });
    }, []);

    const handleFormChange = useCallback(
        (patch) => {
            setForm((prev) => ({ ...prev, ...patch }));
            Object.keys(patch || {}).forEach((field) => clearFieldError(field));
        },
        [clearFieldError]
    );

    const handleTemplateChange = useCallback(
        (index, patch) => {
            setForm((prev) => {
                const articles = [...(prev.template.articles || [])];
                articles[index] = { ...articles[index], ...patch };
                return { ...prev, template: { ...prev.template, articles } };
            });
            Object.keys(patch || {}).forEach((field) => clearArticleError(index, field));
            if (fieldErrors.articles) {
                clearFieldError("articles");
            }
        },
        [clearArticleError, clearFieldError, fieldErrors.articles]
    );

    const handleTemplateDateChange = useCallback(
        (field, value) => {
            setForm((prev) => ({
                ...prev,
                template: { ...prev.template, [field]: value },
            }));
            clearFieldError(field);
        },
        [clearFieldError]
    );

    const handleRemoveArticle = useCallback(
        (index) => {
            setForm((prev) => {
                const articles = [...(prev.template.articles || [])];
                articles.splice(index, 1);
                return { ...prev, template: { ...prev.template, articles } };
            });
            setArticleErrors((prev) => {
                if (!prev[index]) return prev;
                const next = { ...prev };
                delete next[index];
                return next;
            });
            if (fieldErrors.articles) {
                clearFieldError("articles");
            }
        },
        [clearFieldError, fieldErrors.articles]
    );

    const handleAddArticle = useCallback(() => {
        setForm((prev) => ({
            ...prev,
            template: {
                ...prev.template,
                articles: [
                    ...(prev.template.articles || []),
                    {
                        articleId: "",
                        amount: "1",
                        articleDiscount: "0",
                        articleDiscountReason: "",
                    },
                ],
            },
        }));
        if (fieldErrors.articles) {
            clearFieldError("articles");
        }
    }, [clearFieldError, fieldErrors.articles]);

    const handleSave = useCallback(async () => {
        const payload = toPayload(form, profil?.baseCurrency);
        const nextErrors = {};
        const nextArticleErrors = {};
        if (!payload.customerId) {
            nextErrors.customerId = "Bitte Kunden auswaehlen.";
        }
        if (!payload.template.articles.length) {
            nextErrors.articles = "Mindestens eine Position ist erforderlich.";
            (form.template.articles || []).forEach((item, index) => {
                if (!item.articleId) {
                    nextArticleErrors[index] = {
                        ...(nextArticleErrors[index] || {}),
                        articleId: "Bitte Artikel auswählen.",
                    };
                }
            });
        }
        if (Object.keys(nextErrors).length > 0) {
            setFieldErrors(nextErrors);
            setArticleErrors(nextArticleErrors);
            setToast({ open: true, severity: "error", msg: "Bitte prüfe die markierten Felder." });
            return;
        }
        setFieldErrors({});
        setArticleErrors({});
        setSaving(true);
        try {
            if (dialogMode === "edit" && form.id) {
                const res = await updateRecurringInvoice(form.id, payload);
                setEntries((prev) => prev.map((item) => (item.id === res.data.id ? res.data : item)));
                setToast({ open: true, severity: "success", msg: "Abo aktualisiert." });
            } else {
                const res = await createRecurringInvoice(payload);
                setEntries((prev) => [res.data, ...prev]);
                setToast({ open: true, severity: "success", msg: "Abo erstellt." });
            }
            setDialogOpen(false);
        } catch (err) {
            logError("Fehler beim Speichern der wiederkehrenden Rechnung", err, { component: "RecurringInvoiceManagement" });
            const backendError = err?.response?.data;
            const details = Array.isArray(backendError?.details) ? backendError.details : null;
            if (details && details.length > 0) {
                const nextFieldErrors = {};
                const nextItemErrors = {};
                details.forEach((detail) => {
                    const rawField = detail?.field;
                    const message = detail?.message;
                    if (!rawField || !message) return;
                    if (rawField.startsWith("template.articles")) {
                        const indexMatch = /^template\.articles\[(\d+)\]\.(.+)$/.exec(rawField);
                        if (indexMatch) {
                            const index = Number(indexMatch[1]);
                            const field = indexMatch[2];
                            nextItemErrors[index] = {
                                ...(nextItemErrors[index] || {}),
                                [field]: message,
                            };
                            return;
                        }
                        nextFieldErrors.articles = message;
                        return;
                    }
                    if (rawField.startsWith("template.")) {
                        const field = rawField.replace("template.", "");
                        if (field === "dueDateAfterInvoiceDate") {
                            nextFieldErrors.dueDate = message;
                            return;
                        }
                        nextFieldErrors[field] = message;
                        return;
                    }
                    if (rawField === "customerId") {
                        nextFieldErrors.customerId = message;
                        return;
                    }
                    nextFieldErrors[rawField] = message;
                });
                setFieldErrors(nextFieldErrors);
                setArticleErrors(nextItemErrors);
                setToast({
                    open: true,
                    severity: "error",
                    msg: backendError?.message || "Bitte prüfe die markierten Felder.",
                });
                return;
            }
            setToast({ open: true, severity: "error", msg: "Speichern fehlgeschlagen." });
        } finally {
            setSaving(false);
        }
    }, [dialogMode, form, profil?.baseCurrency]);

    const handleDelete = useCallback(
        async (id) => {
            if (!id) return;
            const confirm = window.confirm("Wiederkehrende Rechnung wirklich löschen?");
            if (!confirm) return;
            try {
                await deleteRecurringInvoice(id);
                setEntries((prev) => prev.filter((item) => item.id !== id));
                setToast({ open: true, severity: "success", msg: "Abo gelöscht." });
            } catch (err) {
                logError("Fehler beim Löschen der wiederkehrenden Rechnung", err, { component: "RecurringInvoiceManagement" });
                setToast({ open: true, severity: "error", msg: "Löschen fehlgeschlagen." });
            }
        },
        []
    );

    const handleToggleLogs = useCallback((id) => {
        setExpandedLogs((prev) => (prev === id ? null : id));
    }, []);

    const customerLookup = useMemo(() => {
        const map = new Map();
        customers.forEach((c) => {
            const label = [c.name, c.lastname].filter(Boolean).join(" ") || c.email || "Unbekannt";
            map.set(c.id, label);
        });
        return map;
    }, [customers]);

    const sortedArticles = useMemo(() => sortArticlesByName(articles), [articles]);

    return {
        entries,
        loading,
        error,
        customers,
        articles,
        profil,
        dialogOpen,
        dialogMode,
        form,
        saving,
        fieldErrors,
        articleErrors,
        expandedLogs,
        toast,
        customerLookup,
        sortedArticles,
        setToast,
        setDialogOpen,
        loadData,
        openCreateDialog,
        openEditDialog,
        closeDialog,
        handleFormChange,
        handleTemplateChange,
        handleTemplateDateChange,
        handleRemoveArticle,
        handleAddArticle,
        handleSave,
        handleDelete,
        handleToggleLogs,
    };
}
